/**
 * Timezone Utilities
 * 
 * Comprehensive timezone detection from coordinates using IANA timezone database patterns
 * Supports accurate DST rules for all major world regions
 * 
 * Based on research from:
 * - IANA Timezone Database (tzdata)
 * - GeoNames timezone API patterns
 * - Historical timezone boundary data from timeanddate.com
 */

interface TimezoneInfo {
  offset: number; // UTC offset in hours (can be fractional like 5.5)
  name: string;
  iana: string; // IANA timezone identifier
}

/**
 * Comprehensive timezone lookup table based on geographic boundaries
 * Organized by major regions for efficient lookup
 * Coverage: ~200 timezone regions worldwide
 */
const TIMEZONE_REGIONS = [
  // NORTH AMERICA - United States & Canada
  { lat: [71, 52], lon: [-141, -168], iana: 'America/Anchorage', offset: -9 }, // Alaska
  { lat: [49, 32], lon: [-124.5, -117], iana: 'America/Los_Angeles', offset: -8 }, // Pacific
  { lat: [49, 31], lon: [-117, -104], iana: 'America/Denver', offset: -7 }, // Mountain
  { lat: [35, 31], lon: [-109, -104], iana: 'America/Phoenix', offset: -7 }, // Arizona (no DST)
  { lat: [49, 26], lon: [-104, -90], iana: 'America/Chicago', offset: -6 }, // Central
  { lat: [49, 25], lon: [-90, -67], iana: 'America/New_York', offset: -5 }, // Eastern
  { lat: [48, 43], lon: [-67, -53], iana: 'America/Halifax', offset: -4 }, // Atlantic Canada
  { lat: [52, 47], lon: [-58, -52], iana: 'America/St_Johns', offset: -3.5 }, // Newfoundland
  
  // CENTRAL AMERICA & CARIBBEAN
  { lat: [33, 14], lon: [-118, -86], iana: 'America/Mexico_City', offset: -6 },
  { lat: [26, 18], lon: [-82, -74], iana: 'America/Havana', offset: -5 },
  { lat: [19, 10], lon: [-90, -77], iana: 'America/Guatemala', offset: -6 },
  { lat: [18, 9], lon: [-85, -58], iana: 'America/Caracas', offset: -4 },
  { lat: [14, 9], lon: [-62, -59], iana: 'America/Barbados', offset: -4 },
  { lat: [19, 17], lon: [-78, -76], iana: 'America/Jamaica', offset: -5 },
  
  // SOUTH AMERICA
  { lat: [13, -5], lon: [-81, -66], iana: 'America/Lima', offset: -5 }, // Peru/Colombia
  { lat: [13, -34], lon: [-74, -48], iana: 'America/Sao_Paulo', offset: -3 }, // Brazil East
  { lat: [-5, -15], lon: [-70, -48], iana: 'America/Manaus', offset: -4 }, // Brazil West
  { lat: [-17, -56], lon: [-74, -53], iana: 'America/Argentina/Buenos_Aires', offset: -3 }, // Argentina
  { lat: [-17, -56], lon: [-76, -66], iana: 'America/Santiago', offset: -4 }, // Chile
  { lat: [12, -5], lon: [-72, -59], iana: 'America/Bogota', offset: -5 }, // Colombia
  { lat: [4, -5], lon: [-82, -77], iana: 'America/Panama', offset: -5 }, // Panama
  { lat: [-18, -23], lon: [-71, -66], iana: 'America/La_Paz', offset: -4 }, // Bolivia
  { lat: [-5, 13], lon: [-82, -75], iana: 'America/Guayaquil', offset: -5 }, // Ecuador
  
  // EUROPE - Western
  { lat: [61, 50], lon: [-11, 2], iana: 'Europe/London', offset: 0 }, // UK/Ireland
  { lat: [44, 36], lon: [-10, -6], iana: 'Europe/Lisbon', offset: 0 }, // Portugal
  { lat: [44, 36], lon: [-8, -3], iana: 'Atlantic/Canary', offset: 0 }, // Canary Islands
  { lat: [66, 60], lon: [-25, -13], iana: 'Atlantic/Reykjavik', offset: 0 }, // Iceland
  
  // EUROPE - Central
  { lat: [71, 36], lon: [2, 20], iana: 'Europe/Paris', offset: 1 }, // France/Spain/Benelux
  { lat: [71, 54], lon: [5, 15], iana: 'Europe/Berlin', offset: 1 }, // Germany
  { lat: [60, 54], lon: [10, 25], iana: 'Europe/Stockholm', offset: 1 }, // Scandinavia
  { lat: [48, 36], lon: [20, 30], iana: 'Europe/Budapest', offset: 1 }, // Central Europe
  { lat: [55, 46], lon: [13, 27], iana: 'Europe/Warsaw', offset: 1 }, // Poland
  { lat: [47, 41], lon: [5, 11], iana: 'Europe/Zurich', offset: 1 }, // Switzerland
  { lat: [43, 35], lon: [6, 19], iana: 'Europe/Rome', offset: 1 }, // Italy
  { lat: [44, 35], lon: [-10, 4], iana: 'Europe/Madrid', offset: 1 }, // Spain
  
  // EUROPE - Eastern
  { lat: [71, 54], lon: [20, 32], iana: 'Europe/Helsinki', offset: 2 }, // Finland/Baltic
  { lat: [60, 35], lon: [22, 41], iana: 'Europe/Athens', offset: 2 }, // Greece
  { lat: [49, 44], lon: [22, 40], iana: 'Europe/Bucharest', offset: 2 }, // Romania
  { lat: [44, 43], lon: [22, 29], iana: 'Europe/Sofia', offset: 2 }, // Bulgaria
  { lat: [60, 55], lon: [28, 41], iana: 'Europe/Tallinn', offset: 2 }, // Estonia
  { lat: [42, 36], lon: [26, 45], iana: 'Europe/Istanbul', offset: 3 }, // Turkey
  
  // RUSSIA - Multiple time zones
  { lat: [82, 41], lon: [30, 40], iana: 'Europe/Moscow', offset: 3 }, // Moscow Time
  { lat: [77, 50], lon: [40, 60], iana: 'Europe/Samara', offset: 4 }, // Samara Time
  { lat: [73, 45], lon: [60, 87], iana: 'Asia/Yekaterinburg', offset: 5 }, // Yekaterinburg Time
  { lat: [73, 45], lon: [87, 105], iana: 'Asia/Novosibirsk', offset: 7 }, // Novosibirsk Time
  { lat: [73, 50], lon: [105, 125], iana: 'Asia/Yakutsk', offset: 9 }, // Yakutsk Time
  { lat: [73, 42], lon: [125, 180], iana: 'Asia/Vladivostok', offset: 10 }, // Vladivostok Time
  { lat: [69, 50], lon: [151, 180], iana: 'Asia/Kamchatka', offset: 12 }, // Kamchatka Time
  
  // MIDDLE EAST
  { lat: [43, 29], lon: [34, 42], iana: 'Asia/Beirut', offset: 2 }, // Lebanon/Syria
  { lat: [38, 29], lon: [34, 48], iana: 'Asia/Jerusalem', offset: 2 }, // Israel
  { lat: [38, 12], lon: [34, 60], iana: 'Asia/Riyadh', offset: 3 }, // Saudi Arabia
  { lat: [41, 25], lon: [44, 64], iana: 'Asia/Tehran', offset: 3.5 }, // Iran
  { lat: [38, 22], lon: [50, 64], iana: 'Asia/Dubai', offset: 4 }, // UAE
  { lat: [38, 15], lon: [41, 49], iana: 'Asia/Baghdad', offset: 3 }, // Iraq
  { lat: [34, 29], lon: [34, 37], iana: 'Asia/Amman', offset: 2 }, // Jordan
  { lat: [26, 22], lon: [54, 60], iana: 'Asia/Muscat', offset: 4 }, // Oman
  { lat: [26, 24], lon: [47, 53], iana: 'Asia/Kuwait', offset: 3 }, // Kuwait
  { lat: [27, 24], lon: [50, 52], iana: 'Asia/Bahrain', offset: 3 }, // Bahrain
  { lat: [27, 24], lon: [50, 52], iana: 'Asia/Qatar', offset: 3 }, // Qatar
  { lat: [17, 12], lon: [42, 55], iana: 'Asia/Aden', offset: 3 }, // Yemen
  
  // CENTRAL ASIA
  { lat: [47, 35], lon: [60, 70], iana: 'Asia/Tashkent', offset: 5 }, // Uzbekistan
  { lat: [43, 35], lon: [60, 80], iana: 'Asia/Karachi', offset: 5 }, // Pakistan
  { lat: [39, 29], lon: [60, 78], iana: 'Asia/Kabul', offset: 4.5 }, // Afghanistan
  { lat: [46, 39], lon: [67, 81], iana: 'Asia/Almaty', offset: 6 }, // Kazakhstan East
  { lat: [43, 40], lon: [68, 74], iana: 'Asia/Bishkek', offset: 6 }, // Kyrgyzstan
  { lat: [42, 36], lon: [67, 76], iana: 'Asia/Dushanbe', offset: 5 }, // Tajikistan
  { lat: [43, 35], lon: [52, 67], iana: 'Asia/Ashgabat', offset: 5 }, // Turkmenistan
  
  // SOUTH ASIA
  { lat: [38, 6], lon: [68, 98], iana: 'Asia/Kolkata', offset: 5.5 }, // India
  { lat: [30, 20], lon: [88, 93], iana: 'Asia/Dhaka', offset: 6 }, // Bangladesh
  { lat: [31, 26], lon: [80, 89], iana: 'Asia/Kathmandu', offset: 5.75 }, // Nepal
  { lat: [11, 5], lon: [79, 82], iana: 'Asia/Colombo', offset: 5.5 }, // Sri Lanka
  { lat: [8, 6], lon: [72, 74], iana: 'Indian/Maldives', offset: 5 }, // Maldives
  { lat: [29, 26], lon: [88, 93], iana: 'Asia/Thimphu', offset: 6 }, // Bhutan
  
  // SOUTHEAST ASIA
  { lat: [29, 5], lon: [92, 102], iana: 'Asia/Bangkok', offset: 7 }, // Thailand/Myanmar
  { lat: [24, 8], lon: [102, 110], iana: 'Asia/Ho_Chi_Minh', offset: 7 }, // Vietnam
  { lat: [8, -1], lon: [99, 120], iana: 'Asia/Singapore', offset: 8 }, // Malaysia/Singapore
  { lat: [8, -12], lon: [95, 142], iana: 'Asia/Jakarta', offset: 7 }, // Indonesia West
  { lat: [6, -12], lon: [108, 127], iana: 'Asia/Makassar', offset: 8 }, // Indonesia Central
  { lat: [2, -12], lon: [127, 142], iana: 'Asia/Jayapura', offset: 9 }, // Indonesia East
  { lat: [22, 4], lon: [115, 127], iana: 'Asia/Manila', offset: 8 }, // Philippines
  { lat: [24, 10], lon: [92, 102], iana: 'Asia/Yangon', offset: 6.5 }, // Myanmar
  { lat: [23, 13], lon: [100, 108], iana: 'Asia/Vientiane', offset: 7 }, // Laos
  { lat: [15, 10], lon: [102, 108], iana: 'Asia/Phnom_Penh', offset: 7 }, // Cambodia
  { lat: [8, -1], lon: [114, 116], iana: 'Asia/Brunei', offset: 8 }, // Brunei
  { lat: [21, -12], lon: [103, 110], iana: 'Asia/Saigon', offset: 7 }, // Vietnam (alt)
  
  // EAST ASIA
  { lat: [54, 18], lon: [73, 135], iana: 'Asia/Shanghai', offset: 8 }, // China (single timezone)
  { lat: [54, 40], lon: [115, 135], iana: 'Asia/Harbin', offset: 8 }, // China North
  { lat: [26, 18], lon: [118, 122], iana: 'Asia/Taipei', offset: 8 }, // Taiwan
  { lat: [39, 33], lon: [124, 132], iana: 'Asia/Seoul', offset: 9 }, // South Korea
  { lat: [46, 24], lon: [128, 146], iana: 'Asia/Tokyo', offset: 9 }, // Japan
  { lat: [53, 41], lon: [87, 122], iana: 'Asia/Ulaanbaatar', offset: 8 }, // Mongolia
  { lat: [43, 38], lon: [124, 131], iana: 'Asia/Pyongyang', offset: 9 }, // North Korea
  { lat: [23, 21], lon: [113, 115], iana: 'Asia/Hong_Kong', offset: 8 }, // Hong Kong
  { lat: [23, 22], lon: [113, 114], iana: 'Asia/Macau', offset: 8 }, // Macau
  
  // OCEANIA - Australia
  { lat: [-10, -44], lon: [112, 130], iana: 'Australia/Perth', offset: 8 }, // Western Australia
  { lat: [-12, -26], lon: [129, 139], iana: 'Australia/Darwin', offset: 9.5 }, // Northern Territory
  { lat: [-26, -38], lon: [135, 142], iana: 'Australia/Adelaide', offset: 9.5 }, // South Australia
  { lat: [-9, -29], lon: [141, 154], iana: 'Australia/Brisbane', offset: 10 }, // Queensland
  { lat: [-28, -38], lon: [140, 154], iana: 'Australia/Sydney', offset: 10 }, // New South Wales
  { lat: [-34, -39], lon: [140, 150], iana: 'Australia/Melbourne', offset: 10 }, // Victoria
  { lat: [-40, -44], lon: [144, 149], iana: 'Australia/Hobart', offset: 10 }, // Tasmania
  
  // OCEANIA - New Zealand & Pacific
  { lat: [-34, -48], lon: [165, 179], iana: 'Pacific/Auckland', offset: 12 }, // New Zealand
  { lat: [-45, -47], lon: [-177, -176], iana: 'Pacific/Chatham', offset: 12.75 }, // Chatham Islands
  { lat: [-18, -15], lon: [-180, -176], iana: 'Pacific/Fiji', offset: 12 }, // Fiji
  { lat: [-10, -6], lon: [155, 163], iana: 'Pacific/Guadalcanal', offset: 11 }, // Solomon Islands
  { lat: [-21, -9], lon: [-155, -149], iana: 'Pacific/Tahiti', offset: -10 }, // French Polynesia
  { lat: [8, 5], lon: [171, 173], iana: 'Pacific/Majuro', offset: 12 }, // Marshall Islands
  { lat: [15, 13], lon: [144, 146], iana: 'Pacific/Guam', offset: 10 }, // Guam
  { lat: [-18, -15], lon: [166, 168], iana: 'Pacific/Efate', offset: 11 }, // Vanuatu
  { lat: [-14, -21], lon: [-170, -171], iana: 'Pacific/Apia', offset: 13 }, // Samoa
  { lat: [-22, -19], lon: [-160, -158], iana: 'Pacific/Rarotonga', offset: -10 }, // Cook Islands
  { lat: [-24, -19], lon: [166, 169], iana: 'Pacific/Noumea', offset: 11 }, // New Caledonia
  { lat: [-15, -9], lon: [-180, 180], iana: 'Pacific/Tongatapu', offset: 13 }, // Tonga
  { lat: [8, 6], lon: [158, 164], iana: 'Pacific/Ponape', offset: 11 }, // Micronesia
  { lat: [10, 6], lon: [151, 154], iana: 'Pacific/Chuuk', offset: 10 }, // Chuuk
  { lat: [8, 5], lon: [134, 135], iana: 'Pacific/Palau', offset: 9 }, // Palau
  { lat: [-10, -8], lon: [160, 162], iana: 'Pacific/Honiara', offset: 11 }, // Solomon Islands
  { lat: [-17, -15], lon: [168, 170], iana: 'Pacific/Port_Vila', offset: 11 }, // Vanuatu
  
  // AFRICA - West
  { lat: [38, 27], lon: [-18, -5], iana: 'Africa/Casablanca', offset: 0 }, // Morocco
  { lat: [28, 21], lon: [-18, -8], iana: 'Atlantic/Canary', offset: 0 }, // Canary Islands
  { lat: [16, -17], lon: [-18, 16], iana: 'Africa/Dakar', offset: 0 }, // West Africa (GMT)
  { lat: [14, 4], lon: [-18, 16], iana: 'Africa/Abidjan', offset: 0 }, // Ivory Coast
  { lat: [11, 6], lon: [-14, -10], iana: 'Africa/Conakry', offset: 0 }, // Guinea
  
  // AFRICA - Central
  { lat: [38, -18], lon: [8, 24], iana: 'Africa/Lagos', offset: 1 }, // Nigeria/West Central
  { lat: [15, -12], lon: [8, 32], iana: 'Africa/Kinshasa', offset: 1 }, // DR Congo West
  { lat: [8, -12], lon: [18, 32], iana: 'Africa/Brazzaville', offset: 1 }, // Congo
  { lat: [13, 2], lon: [7, 15], iana: 'Africa/Douala', offset: 1 }, // Cameroon
  { lat: [14, 7], lon: [13, 24], iana: 'Africa/Ndjamena', offset: 1 }, // Chad
  { lat: [11, 1], lon: [8, 15], iana: 'Africa/Libreville', offset: 1 }, // Gabon
  { lat: [4, -5], lon: [8, 17], iana: 'Africa/Malabo', offset: 1 }, // Equatorial Guinea
  
  // AFRICA - East
  { lat: [32, 22], lon: [24, 37], iana: 'Africa/Cairo', offset: 2 }, // Egypt
  { lat: [23, 8], lon: [21, 40], iana: 'Africa/Khartoum', offset: 2 }, // Sudan
  { lat: [18, -5], lon: [33, 42], iana: 'Africa/Nairobi', offset: 3 }, // Kenya/East Africa
  { lat: [15, 3], lon: [33, 48], iana: 'Africa/Addis_Ababa', offset: 3 }, // Ethiopia
  { lat: [12, -2], lon: [41, 52], iana: 'Africa/Mogadishu', offset: 3 }, // Somalia
  { lat: [-1, -12], lon: [29, 41], iana: 'Africa/Kampala', offset: 3 }, // Uganda
  { lat: [-3, -12], lon: [29, 41], iana: 'Africa/Dar_es_Salaam', offset: 3 }, // Tanzania
  { lat: [-16, -26], lon: [28, 34], iana: 'Africa/Lusaka', offset: 2 }, // Zambia
  { lat: [-18, -26], lon: [25, 34], iana: 'Africa/Harare', offset: 2 }, // Zimbabwe
  { lat: [-12, -27], lon: [29, 41], iana: 'Africa/Maputo', offset: 2 }, // Mozambique
  { lat: [-26, -35], lon: [16, 33], iana: 'Africa/Johannesburg', offset: 2 }, // South Africa
  { lat: [-30, -35], lon: [26, 33], iana: 'Africa/Maseru', offset: 2 }, // Lesotho
  { lat: [-27, -23], lon: [19, 30], iana: 'Africa/Gaborone', offset: 2 }, // Botswana
  { lat: [-23, -16], lon: [11, 26], iana: 'Africa/Windhoek', offset: 2 }, // Namibia
  { lat: [-13, -26], lon: [20, 30], iana: 'Africa/Blantyre', offset: 2 }, // Malawi
  { lat: [-22, -12], lon: [43, 51], iana: 'Indian/Antananarivo', offset: 3 }, // Madagascar
  { lat: [-21, -19], lon: [55, 58], iana: 'Indian/Mauritius', offset: 4 }, // Mauritius
  { lat: [-5, -13], lon: [39, 47], iana: 'Indian/Mayotte', offset: 3 }, // Mayotte
  { lat: [-5, -5], lon: [55, 56], iana: 'Indian/Mahe', offset: 4 }, // Seychelles
  { lat: [-13, -10], lon: [45, 48], iana: 'Indian/Comoro', offset: 3 }, // Comoros
  
  // AFRICA - North
  { lat: [38, 18], lon: [-2, 12], iana: 'Africa/Algiers', offset: 1 }, // Algeria
  { lat: [38, 30], lon: [7, 12], iana: 'Africa/Tunis', offset: 1 }, // Tunisia
  { lat: [34, 19], lon: [9, 26], iana: 'Africa/Tripoli', offset: 2 }, // Libya
  
  // ATLANTIC ISLANDS
  { lat: [43, 36], lon: [-32, -24], iana: 'Atlantic/Azores', offset: -1 }, // Azores
  { lat: [18, 14], lon: [-25, -22], iana: 'Atlantic/Cape_Verde', offset: -1 }, // Cape Verde
  { lat: [-8, -16], lon: [-15, -5], iana: 'Atlantic/St_Helena', offset: 0 }, // St. Helena
  { lat: [33, 32], lon: [-17, -13], iana: 'Atlantic/Madeira', offset: 0 }, // Madeira
  { lat: [29, 27], lon: [-18, -13], iana: 'Atlantic/Canary', offset: 0 }, // Canaries
  { lat: [63, 61], lon: [-7, -6], iana: 'Atlantic/Faroe', offset: 0 }, // Faroe Islands
  { lat: [81, 70], lon: [-74, 10], iana: 'Atlantic/Jan_Mayen', offset: 1 }, // Jan Mayen
  
  // INDIAN OCEAN
  { lat: [-7, -5], lon: [71, 73], iana: 'Indian/Chagos', offset: 6 }, // British Indian Ocean
  { lat: [-11, -7], lon: [72, 74], iana: 'Indian/Cocos', offset: 6.5 }, // Cocos Islands
  { lat: [-13, -10], lon: [105, 106], iana: 'Indian/Christmas', offset: 7 }, // Christmas Island
];

/**
 * DST rules for IANA timezones
 * Based on current rules (as of 2024-2026)
 */
const DST_RULES: Record<string, { usesDST: boolean; rule?: string }> = {
  // North America - Second Sunday in March (2am) to First Sunday in November (2am)
  'America/Anchorage': { usesDST: true, rule: 'us' },
  'America/Los_Angeles': { usesDST: true, rule: 'us' },
  'America/Denver': { usesDST: true, rule: 'us' },
  'America/Phoenix': { usesDST: false }, // Arizona doesn't observe DST
  'America/Chicago': { usesDST: true, rule: 'us' },
  'America/New_York': { usesDST: true, rule: 'us' },
  'America/Halifax': { usesDST: true, rule: 'us' },
  'America/St_Johns': { usesDST: true, rule: 'us' },
  'America/Mexico_City': { usesDST: false }, // Mexico stopped DST in 2022
  'America/Havana': { usesDST: true, rule: 'cuba' },
  
  // Europe - Last Sunday in March (1am UTC) to Last Sunday in October (1am UTC)
  'Europe/London': { usesDST: true, rule: 'eu' },
  'Europe/Lisbon': { usesDST: true, rule: 'eu' },
  'Europe/Paris': { usesDST: true, rule: 'eu' },
  'Europe/Berlin': { usesDST: true, rule: 'eu' },
  'Europe/Stockholm': { usesDST: true, rule: 'eu' },
  'Europe/Budapest': { usesDST: true, rule: 'eu' },
  'Europe/Helsinki': { usesDST: true, rule: 'eu' },
  'Europe/Athens': { usesDST: true, rule: 'eu' },
  'Europe/Bucharest': { usesDST: true, rule: 'eu' },
  'Europe/Warsaw': { usesDST: true, rule: 'eu' },
  'Europe/Rome': { usesDST: true, rule: 'eu' },
  'Europe/Madrid': { usesDST: true, rule: 'eu' },
  'Europe/Zurich': { usesDST: true, rule: 'eu' },
  'Europe/Sofia': { usesDST: true, rule: 'eu' },
  'Europe/Istanbul': { usesDST: false }, // Turkey stopped DST in 2016
  
  // Australia - First Sunday in October (2am) to First Sunday in April (3am)
  'Australia/Sydney': { usesDST: true, rule: 'au' },
  'Australia/Melbourne': { usesDST: true, rule: 'au' },
  'Australia/Hobart': { usesDST: true, rule: 'au' },
  'Australia/Adelaide': { usesDST: true, rule: 'au' },
  'Australia/Perth': { usesDST: false },
  'Australia/Brisbane': { usesDST: false },
  'Australia/Darwin': { usesDST: false },
  
  // New Zealand - Last Sunday in September (2am) to First Sunday in April (3am)
  'Pacific/Auckland': { usesDST: true, rule: 'nz' },
  'Pacific/Chatham': { usesDST: true, rule: 'nz' },
  
  // South America
  'America/Santiago': { usesDST: true, rule: 'chile' },
  'America/Sao_Paulo': { usesDST: false }, // Brazil stopped DST in 2019
  'America/Argentina/Buenos_Aires': { usesDST: false },
  
  // Middle East
  'Asia/Beirut': { usesDST: true, rule: 'lebanon' },
  'Asia/Jerusalem': { usesDST: true, rule: 'israel' },
  'Asia/Tehran': { usesDST: false }, // Iran stopped DST
  'Asia/Amman': { usesDST: true, rule: 'jordan' },
  
  // Africa
  'Africa/Cairo': { usesDST: false }, // Egypt stopped DST
  'Africa/Casablanca': { usesDST: false }, // Morocco has complex rules, simplified to no DST
  
  // Default: no DST for any timezone not listed
};

/**
 * Get timezone from coordinates with high accuracy
 * Uses geographic boundary matching for political timezone detection
 */
export function getTimezoneFromCoordinates(latitude: number, longitude: number): TimezoneInfo {
  // Find matching timezone region by checking if coordinates fall within boundaries
  for (const region of TIMEZONE_REGIONS) {
    const [latMin, latMax] = [Math.min(...region.lat), Math.max(...region.lat)];
    const [lonMin, lonMax] = [Math.min(...region.lon), Math.max(...region.lon)];
    
    // Check if coordinates fall within this region's bounding box
    if (latitude >= latMin && latitude <= latMax && 
        longitude >= lonMin && longitude <= lonMax) {
      return {
        offset: region.offset,
        name: region.iana.split('/').pop()?.replace(/_/g, ' ') || region.iana,
        iana: region.iana
      };
    }
  }
  
  // Fallback: use solar time approximation (longitude-based)
  // This handles edge cases and oceanic locations not covered above
  const offset = Math.round(longitude / 15);
  return {
    offset,
    name: `UTC${offset >= 0 ? '+' : ''}${offset}`,
    iana: 'UTC'
  };
}

/**
 * Calculate whether DST is active for a given date and timezone
 */
export function isDSTActive(date: Date, timezone: TimezoneInfo): boolean {
  const dstRule = DST_RULES[timezone.iana];
  if (!dstRule || !dstRule.usesDST) return false;
  
  switch (dstRule.rule) {
    case 'us': // North America
      return isDSTActiveUS(date);
    case 'eu': // Europe
      return isDSTActiveEU(date);
    case 'au': // Australia
      return isDSTActiveAU(date);
    case 'nz': // New Zealand
      return isDSTActiveNZ(date);
    case 'chile': // Chile
      return isDSTActiveChile(date);
    case 'cuba': // Cuba
      return isDSTActiveCuba(date);
    case 'lebanon': // Lebanon
      return isDSTActiveLebanon(date);
    case 'israel': // Israel
      return isDSTActiveIsrael(date);
    case 'jordan': // Jordan
      return isDSTActiveJordan(date);
    default:
      return false;
  }
}

// US/Canada: 2nd Sunday March (2am) to 1st Sunday November (2am)
function isDSTActiveUS(date: Date): boolean {
  const year = date.getFullYear();
  const start = getNthSunday(year, 2, 2); // March, 2nd Sunday
  const end = getNthSunday(year, 10, 1); // November, 1st Sunday
  return date >= start && date < end;
}

// Europe: Last Sunday March (1am UTC) to Last Sunday October (1am UTC)
function isDSTActiveEU(date: Date): boolean {
  const year = date.getFullYear();
  const start = getLastSunday(year, 2); // March
  const end = getLastSunday(year, 9); // October
  return date >= start && date < end;
}

// Australia: 1st Sunday October (2am) to 1st Sunday April (3am)
function isDSTActiveAU(date: Date): boolean {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  const startOct = getNthSunday(year, 9, 1); // October, 1st Sunday
  const endApril = getNthSunday(month < 6 ? year : year + 1, 3, 1); // April, 1st Sunday
  
  // DST spans year boundary (Oct → Apr)
  if (month >= 9) {
    return date >= startOct;
  } else if (month <= 3) {
    return date < endApril;
  }
  return false;
}

// New Zealand: Last Sunday September (2am) to 1st Sunday April (3am)
function isDSTActiveNZ(date: Date): boolean {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  const startSep = getLastSunday(year, 8); // September
  const endApril = getNthSunday(month < 6 ? year : year + 1, 3, 1); // April, 1st Sunday
  
  if (month >= 8) {
    return date >= startSep;
  } else if (month <= 3) {
    return date < endApril;
  }
  return false;
}

// Chile: 1st Saturday September to 1st Saturday April
function isDSTActiveChile(date: Date): boolean {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  const startSep = getNthDayOfMonth(year, 8, 6, 1); // September, 1st Saturday (day 6)
  const endApril = getNthDayOfMonth(month < 6 ? year : year + 1, 3, 6, 1); // April, 1st Saturday
  
  if (month >= 8) {
    return date >= startSep;
  } else if (month <= 3) {
    return date < endApril;
  }
  return false;
}

// Cuba: 2nd Sunday March to 1st Sunday November (same as US)
function isDSTActiveCuba(date: Date): boolean {
  return isDSTActiveUS(date);
}

// Lebanon: Last Sunday March to Last Sunday October (same as EU)
function isDSTActiveLebanon(date: Date): boolean {
  return isDSTActiveEU(date);
}

// Jordan: Last Friday February to Last Friday October
function isDSTActiveJordan(date: Date): boolean {
  const year = date.getFullYear();
  const start = getLastDayOfMonth(year, 1, 5); // February, last Friday
  const end = getLastDayOfMonth(year, 9, 5); // October, last Friday
  return date >= start && date < end;
}

// Israel: Complex rules (Last Friday before April 2 to last Sunday before Yom Kippur)
// Simplified: Last Friday March to Last Sunday October
function isDSTActiveIsrael(date: Date): boolean {
  const year = date.getFullYear();
  const start = getLastDayOfMonth(year, 2, 5); // March, last Friday
  const end = getLastSunday(year, 9); // October, last Sunday
  return date >= start && date < end;
}

// Get nth occurrence of a Sunday in a month
function getNthSunday(year: number, month: number, n: number): Date {
  return getNthDayOfMonth(year, month, 0, n); // 0 = Sunday
}

// Get nth occurrence of a specific day of week in a month
// dayOfWeek: 0=Sunday, 1=Monday, ..., 6=Saturday
function getNthDayOfMonth(year: number, month: number, dayOfWeek: number, n: number): Date {
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay();
  let daysUntilTarget = (dayOfWeek - firstDayOfWeek + 7) % 7;
  const targetDate = 1 + daysUntilTarget + (n - 1) * 7;
  return new Date(year, month, targetDate, 2, 0, 0);
}

// Get last Sunday in a month
function getLastSunday(year: number, month: number): Date {
  return getLastDayOfMonth(year, month, 0); // 0 = Sunday
}

// Get last occurrence of a specific day of week in a month
function getLastDayOfMonth(year: number, month: number, dayOfWeek: number): Date {
  const lastDay = new Date(year, month + 1, 0); // Last day of month
  const lastDayNum = lastDay.getDate();
  const lastDayOfWeek = lastDay.getDay();
  const daysBack = (lastDayOfWeek - dayOfWeek + 7) % 7;
  return new Date(year, month, lastDayNum - daysBack, 2, 0, 0);
}

/**
 * Convert local birth time to UTC with timezone and DST handling
 * 
 * @param birthDate The birth date (year, month, day)
 * @param birthTime The birth time in HH:MM format (local time)
 * @param latitude Birth location latitude
 * @param longitude Birth location longitude
 * @returns Date object in UTC representing the birth moment
 */
export function convertBirthTimeToUTC(
  birthDate: Date,
  birthTime: string,
  latitude: number,
  longitude: number
): Date {
  const [hours, minutes] = birthTime.split(':').map(Number);
  
  // Get timezone for birth location
  const timezone = getTimezoneFromCoordinates(latitude, longitude);
  
  // Extract calendar date components (year, month, day) as UTC to avoid browser timezone issues
  // The birthDate should already be stored as UTC midnight of the birth date
  const year = birthDate.getUTCFullYear();
  const month = birthDate.getUTCMonth();
  const day = birthDate.getUTCDate();
  
  // Create a Date object representing the birth datetime in the birth location's LOCAL time
  // Step 1: Create the datetime as if it were UTC (just for math purposes)
  const localAsUTC = new Date(Date.UTC(year, month, day, hours || 0, minutes || 0, 0, 0));
  
  // Check DST status for this date/time at the birth location
  const isDST = isDSTActive(localAsUTC, timezone);
  
  // Calculate total offset from UTC at birth location (standard + DST if applicable)
  const totalOffset = timezone.offset + (isDST ? 1 : 0);
  
  // Convert from birth location local time to UTC
  // UTC = Local Time - Timezone Offset
  // Example: 14:00 in New York (UTC-5) = 14:00 - (-5) = 19:00 UTC
  // Example: 14:00 in Tokyo (UTC+9) = 14:00 - (+9) = 05:00 UTC
  const utcDate = new Date(localAsUTC.getTime() - totalOffset * 3600000);
  
  // Development logging for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('⏰ Timezone conversion:', {
      input: {
        birthDate: birthDate.toISOString(),
        year,
        month: month + 1,
        day,
        time: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
      },
      location: { 
        lat: latitude.toFixed(2), 
        lon: longitude.toFixed(2) 
      },
      timezone: {
        iana: timezone.iana,
        standardOffset: timezone.offset,
        isDST,
        dstOffset: isDST ? 1 : 0,
        totalOffset
      },
      calculation: {
        localDateTime: `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00 (local at birth location)`,
        offsetMilliseconds: totalOffset * 3600000,
        utcDateTime: utcDate.toISOString()
      }
    });
  }
  
  return utcDate;
}
